import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { 
    X, 
    Calendar, 
    Users, 
    Clock, 
    Edit, 
    Trash2, 
    Plus,
    ChevronLeft,
    ChevronRight,
    CheckSquare,
    Square,
} from 'lucide-react';
import AddRecurrenceModal from './AddRecurrenceModal';
import EditRecurrenceModal from './EditRecurrenceModal';
import BulkEditModal from './BulkEditModal';
import EditCourseModal from './EditCourseModal';

const WEEKDAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKDAY_BY_JS_DAY = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function getSessionWeekdayKey(sessionDate) {
    const datePart = String(sessionDate || '').slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return WEEKDAY_BY_JS_DAY[date.getDay()] || null;
}

export default function RecurrenceManager({
    isOpen,
    course,
    onClose,
    onRecurrenceUpdated,
    initialTab = 'dates',
    embedded = false,
    headerActions = null,
}) {
    const { getAuthenticatedApi } = useAuth();
    const { t, i18n } = useTranslation();
    
    const [loading, setLoading] = useState(true);
    const [allFilteredRecurrences, setAllFilteredRecurrences] = useState([]);
    const [selectedRecurrences, setSelectedRecurrences] = useState([]);
    const [activeWeekdays, setActiveWeekdays] = useState([]);
    const [dateScope, setDateScope] = useState('month'); // month | all
    const [showAllMonthsConfirm, setShowAllMonthsConfirm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    
    // Filtri
    const [filters, setFilters] = useState(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1; // +1 perché getMonth() è 0-based
        return {
            month: `${year}-${String(month).padStart(2, '0')}`, // YYYY-MM
        onlyFuture: true,
        status: 'all' // all, active, cancelled
        };
    });
    
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [selectedRecurrence, setSelectedRecurrence] = useState(null);
    const [courseTab, setCourseTab] = useState(initialTab === 'edit' ? 'edit' : 'dates');

    const isActive = embedded ? Boolean(course) : isOpen;

    const selectedMonthLabel = useMemo(() => {
        const [year, month] = String(filters.month || '').split('-').map(Number);
        if (!year || !month) return filters.month;
        const locale = i18n.language?.startsWith('en') ? 'en-US' : 'it-IT';
        return new Date(year, month - 1, 1).toLocaleDateString(locale, {
            month: 'long',
            year: 'numeric',
        });
    }, [filters.month, i18n.language]);

    useEffect(() => {
        if (isActive) {
            setCourseTab(initialTab === 'edit' ? 'edit' : 'dates');
            setSelectedRecurrences([]);
            setActiveWeekdays([]);
            setDateScope('month');
            setShowAllMonthsConfirm(false);
            setCurrentPage(1);
        }
    }, [isActive, course?.id, initialTab]);

    useEffect(() => {
        if (isActive && course) {
            fetchRecurrences();
        }
    }, [isActive, course, filters, dateScope]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeWeekdays, filters.month, filters.onlyFuture, filters.status, dateScope]);

    const fetchRecurrences = async ({ silent = false } = {}) => {
        try {
            if (!silent) {
                setLoading(true);
            }
            const api = getAuthenticatedApi();

            const query =
                dateScope === 'all'
                    ? `/gym_courses.php?action=list_sessions&all=true&course_id=${course.id}`
                    : `/gym_courses.php?action=list_sessions&month=${filters.month}&course_id=${course.id}`;
            const response = await api.get(query);

            let courseSessions = (response.data.sessions || []).filter(
                (s) => Number(s.course_id) === Number(course.id)
            );
            
            // Applica filtri aggiuntivi
            if (filters.search) {
                const searchTerm = filters.search.toLowerCase();
                courseSessions = courseSessions.filter(s => 
                    s.title?.toLowerCase().includes(searchTerm) ||
                    s.description?.toLowerCase().includes(searchTerm)
                );
            }
            
            if (filters.onlyFuture) {
                // Confronta solo le date (senza ora) per includere anche oggi
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Normalizza a mezzanotte
                courseSessions = courseSessions.filter(s => {
                    const sessionDate = new Date(s.session_date);
                    sessionDate.setHours(0, 0, 0, 0); // Normalizza a mezzanotte
                    return sessionDate >= today;
                });
            }
            
            if (filters.status && filters.status !== 'all') {
                courseSessions = courseSessions.filter(s => s.status === filters.status);
            }
            
            // Ordina le sessioni per data crescente (prima le più vicine)
            courseSessions.sort((a, b) => {
                const dateA = new Date(a.session_date);
                const dateB = new Date(b.session_date);
                return dateA - dateB;
            });

            setAllFilteredRecurrences(courseSessions);

            if (activeWeekdays.length > 0) {
                const weekdayIds = courseSessions
                    .filter((session) =>
                        activeWeekdays.includes(getSessionWeekdayKey(session.session_date))
                    )
                    .map((session) => session.id);
                setSelectedRecurrences(weekdayIds);
            }
            
        } catch (err) {
            console.error('Errore caricamento sessioni:', err);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    const weekdayOptions = useMemo(() => {
        const counts = {};
        allFilteredRecurrences.forEach((session) => {
            const key = getSessionWeekdayKey(session.session_date);
            if (!key) return;
            counts[key] = (counts[key] || 0) + 1;
        });
        return WEEKDAY_ORDER
            .filter((key) => counts[key] > 0)
            .map((key) => ({
                key,
                count: counts[key],
                label: t(`gym.courses.addRecurrenceModal.daysOfWeek.${key}`, {
                    defaultValue: key,
                }),
            }));
    }, [allFilteredRecurrences, t]);

    const visibleRecurrences = useMemo(() => {
        if (activeWeekdays.length === 0) return allFilteredRecurrences;
        return allFilteredRecurrences.filter((session) =>
            activeWeekdays.includes(getSessionWeekdayKey(session.session_date))
        );
    }, [allFilteredRecurrences, activeWeekdays]);

    const filteredTotal = visibleRecurrences.length;
    const totalPages = Math.max(1, Math.ceil(filteredTotal / itemsPerPage) || 1);
    const safePage = Math.min(currentPage, totalPages);
    const recurrences = useMemo(() => {
        const startIndex = (safePage - 1) * itemsPerPage;
        return visibleRecurrences.slice(startIndex, startIndex + itemsPerPage);
    }, [visibleRecurrences, safePage, itemsPerPage]);

    const handleSelectAll = () => {
        const allIds = visibleRecurrences.map((r) => r.id);
        const allSelected =
            allIds.length > 0 && allIds.every((id) => selectedRecurrences.includes(id));
        setSelectedRecurrences(allSelected ? [] : allIds);
    };

    const handleSelectWeekday = (weekdayKey) => {
        const weekdayIds = allFilteredRecurrences
            .filter((session) => getSessionWeekdayKey(session.session_date) === weekdayKey)
            .map((session) => session.id);
        if (weekdayIds.length === 0) return;

        const isFilterActive = activeWeekdays.includes(weekdayKey);
        if (isFilterActive) {
            const nextWeekdays = activeWeekdays.filter((key) => key !== weekdayKey);
            setActiveWeekdays(nextWeekdays);
            setSelectedRecurrences((prev) => prev.filter((id) => !weekdayIds.includes(id)));
            if (nextWeekdays.length === 0) {
                setShowAllMonthsConfirm(false);
                if (dateScope === 'all') {
                    setDateScope('month');
                }
            }
            return;
        }

        setActiveWeekdays((prev) => [...prev, weekdayKey]);
        setSelectedRecurrences((prev) => [...new Set([...prev, ...weekdayIds])]);
    };

    const handleRequestAllMonths = () => {
        if (dateScope === 'all') return;
        setShowAllMonthsConfirm(true);
    };

    const handleConfirmAllMonths = () => {
        setShowAllMonthsConfirm(false);
        setDateScope('all');
    };

    const handleUseMonthScope = () => {
        setShowAllMonthsConfirm(false);
        if (dateScope !== 'month') {
            setDateScope('month');
        }
    };

    const handleSelectRecurrence = (recurrenceId) => {
        setSelectedRecurrences(prev => 
            prev.includes(recurrenceId)
                ? prev.filter(id => id !== recurrenceId)
                : [...prev, recurrenceId]
        );
    };

    const handleBulkDelete = async () => {
        if (selectedRecurrences.length === 0) return;
        
        if (!window.confirm(t('gym.courses.recurrenceManager.messages.deleteConfirm', { count: selectedRecurrences.length }))) {
            return;
        }

        try {
            const api = getAuthenticatedApi();
            
            // Elimina una per una usando l'endpoint esistente
            for (const recurrenceId of selectedRecurrences) {
                await api.delete(`/gym_courses.php?action=delete_session&id=${recurrenceId}`);
            }
            
            setSelectedRecurrences([]);
            fetchRecurrences({ silent: true });
            onRecurrenceUpdated();
            
        } catch (err) {
            console.error('Errore eliminazione lezioni:', err);
            toast.error('Errore durante l\'eliminazione delle lezioni');
        }
    };

    const handleBulkEdit = () => {
        if (selectedRecurrences.length === 0) return;
        setShowBulkModal(true);
    };

    const handleEditRecurrence = (recurrence) => {
        setSelectedRecurrence(recurrence);
        setShowEditModal(true);
    };

    const handleDeleteRecurrence = async (recurrenceId) => {
        if (!window.confirm(t('gym.courses.recurrenceManager.messages.deleteSingleConfirm'))) return;

        try {
            const api = getAuthenticatedApi();
            await api.delete(`/gym_courses.php?action=delete_session&id=${recurrenceId}`);
            
            fetchRecurrences({ silent: true });
            onRecurrenceUpdated();
            
        } catch (err) {
            console.error('Errore eliminazione lezione:', err);
            toast.error('Errore durante l\'eliminazione');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (timeString) => {
        return timeString?.slice(0, 5) || '';
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-800 dark:text-green-400', label: t('gym.courses.recurrenceManager.status.active') },
            cancelled: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-800 dark:text-red-400', label: t('gym.courses.recurrenceManager.status.cancelled') },
            completed: { bg: 'bg-gray-100 dark:bg-gray-900/20', text: 'text-gray-800 dark:text-white/65', label: t('gym.courses.recurrenceManager.status.completed') }
        };
        
        const badge = badges[status] || badges.active;
        
        return (
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    if (!isActive || !course) return null;

    const panel = (
            <>
                {/* Header */}
                <div className={`flex items-center justify-between border-b border-gray-200 dark:border-white/10 ${embedded ? 'px-4 py-4 md:px-6' : 'p-6'}`}>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            {embedded && onClose && (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="mr-1 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 md:hidden dark:text-white/70 dark:hover:bg-white/10"
                                    aria-label={t('gym.courses.list.backToList')}
                                >
                                    <ChevronLeft size={22} />
                                </button>
                            )}
                            <div className="min-w-0">
                                <h3 className="truncate text-xl font-semibold text-gray-900 dark:text-white">
                                    {embedded ? course.title : `${t('gym.courses.recurrenceManager.title')}: ${course.title}`}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                                    {t('gym.courses.recurrenceManager.subtitle', { 
                                        count: filteredTotal, 
                                        current: safePage, 
                                        total: totalPages 
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="ml-3 flex flex-shrink-0 items-center gap-2">
                        {headerActions}
                        {!embedded && (
                            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        )}
                    </div>
                </div>

                <div className={`flex border-b border-gray-200 dark:border-white/10 ${embedded ? 'px-4 md:px-6' : 'px-6'}`}>
                    <button
                        type="button"
                        onClick={() => setCourseTab('edit')}
                        className={`border-b-2 px-4 py-3 text-sm font-medium ${
                            courseTab === 'edit'
                                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                                : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-white/60 dark:hover:text-white'
                        }`}
                    >
                        {t('gym.courses.list.editTab', { defaultValue: 'Modifica' })}
                    </button>
                    <button
                        type="button"
                        onClick={() => setCourseTab('dates')}
                        className={`border-b-2 px-4 py-3 text-sm font-medium ${
                            courseTab === 'dates'
                                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                                : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-white/60 dark:hover:text-white'
                        }`}
                    >
                        {t('gym.courses.list.datesTab', { defaultValue: 'Sessioni' })}
                    </button>
                </div>

                <div className={`min-h-0 flex-1 overflow-y-auto ${embedded ? 'p-4 md:p-6' : 'p-6'}`}>
                    {courseTab === 'edit' && (
                        <EditCourseModal
                            isOpen
                            embedded
                            course={course}
                            onClose={() => {}}
                            onSuccess={onRecurrenceUpdated}
                        />
                    )}
                    {courseTab === 'dates' && (
                    <>
                    {/* Filtri e Azioni */}
                    <div className="flex flex-col lg:flex-row gap-4 mb-6">
                        {/* Filtri */}
                        <div className={`flex flex-wrap gap-3 flex-1 ${dateScope === 'all' ? 'opacity-50' : ''}`}>
                            {/* Selettore Mese Migliorato */}
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    disabled={dateScope === 'all'}
                                    onClick={() => {
                                        const [year, month] = filters.month.split('-').map(Number);
                                        const prevMonth = month === 1 ? 12 : month - 1;
                                        const prevYear = month === 1 ? year - 1 : year;
                                        setFilters(prev => ({ 
                                            ...prev, 
                                            month: `${prevYear}-${String(prevMonth).padStart(2, '0')}`
                                        }));
                                    }}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors disabled:cursor-not-allowed"
                                    title="Mese precedente"
                                >
                                    <ChevronLeft size={20} className="text-gray-600 dark:text-white/65" />
                                </button>
                                
                                <select
                                value={filters.month}
                                disabled={dateScope === 'all'}
                                onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-medium min-w-[180px] disabled:cursor-not-allowed"
                                >
                                    {(() => {
                                        const options = [];
                                        const today = new Date();
                                        const currentYear = today.getFullYear();
                                        const currentMonth = today.getMonth();
                                        
                                        // Genera opzioni per 6 mesi passati e 12 mesi futuri
                                        for (let i = -6; i <= 12; i++) {
                                            const targetDate = new Date(currentYear, currentMonth + i, 1);
                                            const year = targetDate.getFullYear();
                                            const month = targetDate.getMonth() + 1; // +1 perché getMonth() è 0-based
                                            
                                            // Costruisci value manualmente senza conversione UTC
                                            const value = `${year}-${String(month).padStart(2, '0')}`;
                                            
                                            const label = targetDate.toLocaleDateString('it-IT', { 
                                                year: 'numeric', 
                                                month: 'long' 
                                            });
                                            const isCurrentMonth = i === 0;
                                            options.push(
                                                <option key={value} value={value}>
                                                    {isCurrentMonth ? `📍 ${label}` : label}
                                                </option>
                                            );
                                        }
                                        return options;
                                    })()}
                                </select>
                                
                                <button
                                    type="button"
                                    disabled={dateScope === 'all'}
                                    onClick={() => {
                                        const [year, month] = filters.month.split('-').map(Number);
                                        const nextMonth = month === 12 ? 1 : month + 1;
                                        const nextYear = month === 12 ? year + 1 : year;
                                        setFilters(prev => ({ 
                                            ...prev, 
                                            month: `${nextYear}-${String(nextMonth).padStart(2, '0')}`
                                        }));
                                    }}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors disabled:cursor-not-allowed"
                                    title="Mese successivo"
                                >
                                    <ChevronRight size={20} className="text-gray-600 dark:text-white/65" />
                                </button>
                                
                                <button
                                    type="button"
                                    disabled={dateScope === 'all'}
                                    onClick={() => {
                                        const today = new Date();
                                        const year = today.getFullYear();
                                        const month = today.getMonth() + 1;
                                        setFilters(prev => ({ 
                                            ...prev, 
                                            month: `${year}-${String(month).padStart(2, '0')}`
                                        }));
                                    }}
                                    className="px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors disabled:cursor-not-allowed"
                                    title="Torna al mese corrente"
                                >
                                    Oggi
                                </button>
                            </div>
                            
                            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-white/85">
                                <input
                                    type="checkbox"
                                    checked={filters.onlyFuture}
                                    onChange={(e) => setFilters(prev => ({ ...prev, onlyFuture: e.target.checked }))}
                                    className="w-4 h-4 text-indigo-600 rounded"
                                />
                                {t('gym.courses.recurrenceManager.filters.onlyFuture')}
                            </label>
                        </div>

                        {/* Azioni */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"
                            >
                                <Plus size={16} />
                                {t('gym.courses.recurrenceManager.actions.add')}
                            </button>
                            
                            {selectedRecurrences.length > 0 && (
                                <>
                                    <button
                                        onClick={handleBulkEdit}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                                    >
                                        <Edit size={16} />
                                        {t('gym.courses.recurrenceManager.actions.bulkEdit', { count: selectedRecurrences.length })}
                                    </button>
                                    
                                    <button
                                        onClick={handleBulkDelete}
                                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
                                    >
                                        <Trash2 size={16} />
                                        {t('gym.courses.recurrenceManager.actions.bulkDelete', { count: selectedRecurrences.length })}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {weekdayOptions.length > 0 && (
                        <div className="mb-4">
                            <p className="mb-2 text-xs font-medium text-gray-500 dark:text-white/60">
                                {t('gym.courses.recurrenceManager.actions.selectByWeekday')}
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                                {weekdayOptions.map(({ key, count, label }) => {
                                    const isWeekdayActive = activeWeekdays.includes(key);
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => handleSelectWeekday(key)}
                                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                                isWeekdayActive
                                                    ? 'border-indigo-500 bg-indigo-100 text-indigo-800 dark:border-indigo-400 dark:bg-indigo-900/40 dark:text-indigo-100'
                                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:bg-gray-800 dark:text-white/85 dark:hover:bg-white/10'
                                            }`}
                                        >
                                            {label} ({count})
                                        </button>
                                    );
                                })}

                                {activeWeekdays.length > 0 && (
                                    <div className="ml-0 flex flex-wrap items-center gap-2 sm:ml-2">
                                        <span className="text-xs text-gray-500 dark:text-white/55">
                                            {t('gym.courses.recurrenceManager.scope.label')}:
                                        </span>
                                        <button
                                            type="button"
                                            onClick={handleUseMonthScope}
                                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                                dateScope === 'month'
                                                    ? 'border-indigo-500 bg-indigo-100 text-indigo-800 dark:border-indigo-400 dark:bg-indigo-900/40 dark:text-indigo-100'
                                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:bg-gray-800 dark:text-white/85 dark:hover:bg-white/10'
                                            }`}
                                        >
                                            {t('gym.courses.recurrenceManager.scope.month', { month: selectedMonthLabel })}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleRequestAllMonths}
                                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                                dateScope === 'all'
                                                    ? 'border-indigo-500 bg-indigo-100 text-indigo-800 dark:border-indigo-400 dark:bg-indigo-900/40 dark:text-indigo-100'
                                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:bg-gray-800 dark:text-white/85 dark:hover:bg-white/10'
                                            }`}
                                        >
                                            {t('gym.courses.recurrenceManager.scope.allMonths')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {showAllMonthsConfirm && (
                                <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/80 p-3 dark:border-indigo-500/30 dark:bg-indigo-950/40">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {t('gym.courses.recurrenceManager.scope.confirmTitle')}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-600 dark:text-white/70">
                                        {t('gym.courses.recurrenceManager.scope.confirmMessage', {
                                            month: selectedMonthLabel,
                                        })}
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setShowAllMonthsConfirm(false)}
                                            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:bg-gray-800 dark:text-white/85"
                                        >
                                            {t('gym.courses.recurrenceManager.scope.cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleConfirmAllMonths}
                                            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                                        >
                                            {t('gym.courses.recurrenceManager.scope.confirm')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tabella */}
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : filteredTotal === 0 ? (
                        <div className="py-10 text-center text-sm text-gray-500 dark:text-white/60">
                            {t('gym.courses.recurrenceManager.messages.noRecurrences')}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-white/10">
                                        <th className="text-left py-3 px-4">
                                            <button
                                                onClick={handleSelectAll}
                                                className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-white/85"
                                            >
                                                {visibleRecurrences.length > 0 &&
                                                visibleRecurrences.every((r) =>
                                                    selectedRecurrences.includes(r.id)
                                                ) ? (
                                                    <CheckSquare size={16} className="text-indigo-600" />
                                                ) : (
                                                    <Square size={16} />
                                                )}
                                                {t('gym.courses.recurrenceManager.actions.selectAll')}
                                            </button>
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.date')}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.time')}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.participants')}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.status')}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.actions')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recurrences.map((recurrence) => (
                                        <tr
                                            key={recurrence.id}
                                            onClick={() => handleEditRecurrence(recurrence)}
                                            className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/[0.06]"
                                        >
                                            <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => handleSelectRecurrence(recurrence.id)}
                                                    className="flex items-center gap-2"
                                                >
                                                    {selectedRecurrences.includes(recurrence.id) ? (
                                                        <CheckSquare size={16} className="text-indigo-600" />
                                                    ) : (
                                                        <Square size={16} className="text-gray-400" />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <Calendar size={16} className="text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {formatDate(recurrence.session_date)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <Clock size={16} className="text-gray-400" />
                                                    <span className="text-sm text-gray-900 dark:text-white">
                                                        {formatTime(recurrence.start_time)} - {formatTime(recurrence.end_time)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <Users size={16} className="text-gray-400" />
                                                    <span className="text-sm text-gray-900 dark:text-white">
                                                        {recurrence.current_participants || 0} / {recurrence.max_participants || '∞'}
                                                    </span>
                                                    {Number(recurrence.waitlist_count) > 0 && (
                                                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                                            {recurrence.waitlist_count} in lista
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                {getStatusBadge(recurrence.status)}
                                            </td>
                                            <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleEditRecurrence(recurrence)}
                                                        className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                                        title="Modifica"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteRecurrence(recurrence.id)}
                                                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                                        title="Elimina"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Paginazione */}
                    {totalPages > 1 && filteredTotal > 0 && (
                        <div className="flex items-center justify-between mt-6">
                            <div className="text-sm text-gray-500 dark:text-white/65">
                                {t('gym.courses.recurrenceManager.table.pagination.showing', {
                                    start: ((safePage - 1) * itemsPerPage) + 1,
                                    end: Math.min(safePage * itemsPerPage, filteredTotal),
                                    total: filteredTotal
                                })}
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={safePage === 1}
                                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                
                                <span className="px-3 py-1 text-sm text-gray-700 dark:text-white/85">
                                    {safePage} di {totalPages}
                                </span>
                                
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={safePage === totalPages}
                                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </div>

            {/* Modal Aggiungi Lezioni */}
            {showAddModal && (
                <AddRecurrenceModal
                    isOpen={showAddModal}
                    course={course}
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        fetchRecurrences({ silent: true });
                        onRecurrenceUpdated();
                    }}
                />
            )}

            {/* Modal Modifica Lezione */}
            {showEditModal && selectedRecurrence && (
                <EditRecurrenceModal
                    isOpen={showEditModal}
                    recurrence={selectedRecurrence}
                    course={course}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedRecurrence(null);
                        // Silent refresh: iscritti/lista possono essere cambiati anche senza Salva
                        fetchRecurrences({ silent: true });
                        onRecurrenceUpdated();
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedRecurrence(null);
                        fetchRecurrences({ silent: true });
                        onRecurrenceUpdated();
                    }}
                />
            )}

            {/* Modal Modifica Multipla */}
            {showBulkModal && (
                <BulkEditModal
                    isOpen={showBulkModal}
                    selectedRecurrences={selectedRecurrences}
                    onClose={() => {
                        setShowBulkModal(false);
                        setSelectedRecurrences([]);
                        fetchRecurrences({ silent: true });
                        onRecurrenceUpdated();
                    }}
                    onSuccess={() => {
                        setShowBulkModal(false);
                        setSelectedRecurrences([]);
                        fetchRecurrences({ silent: true });
                        onRecurrenceUpdated();
                    }}
                />
            )}
            </>
    );

    if (embedded) {
        return (
            <div className="flex h-full min-h-0 flex-col bg-white dark:bg-gray-800">
                {panel}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4">
            <div className="my-8 flex w-full max-w-6xl flex-col rounded-lg bg-white dark:bg-gray-900">
                {panel}
            </div>
        </div>
    );
}
